import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import LegalPlusSystem from './LegalPlusSystem';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ChangePasswordModal from './components/ChangePasswordModal';
import { api } from './utils/apiClient';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hora

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Midnight Court es dark-first por defecto — forzamos el modo en el root
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  // Verificar sesion con el backend al cargar (la cookie HttpOnly se envia automaticamente)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const data = await api.get('/verificar-sesion');
        if (data.autenticado) {
          // Sesion valida — cargar datos minimos del usuario desde localStorage (no sensibles)
          const cached = localStorage.getItem('lp_user_display');
          let display = cached ? JSON.parse(cached) : { id: data.user_id, rol: data.rol };
          // Diner 2026-04-27: si el cache no tiene num_doc pero la sesion del backend si, refrescar.
          if (!display.num_doc && data.num_doc) {
            display = { ...display, id: data.user_id, rol: data.rol, num_doc: data.num_doc, nombre: data.nombre || display.nombre, nickname: data.nickname || display.nickname, email: data.email || display.email };
            localStorage.setItem('lp_user_display', JSON.stringify(display));
          }
          setUserData(display);
          setIsAuthenticated(true);
        }
      } catch (e) {
        // Sesion expirada o no existe — limpiar
        localStorage.removeItem('lp_user_display');
        setIsAuthenticated(false);
        setUserData(null);
      } finally {
        setSessionChecked(true);
      }
    };
    checkSession();
  }, []);

  // Timer de inactividad
  useEffect(() => {
    if (!isAuthenticated) return;

    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        handleLogout();
        alert('Su sesion ha expirado por inactividad.');
      }, INACTIVITY_TIMEOUT);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [isAuthenticated]);

  const handleLogin = (user) => {
    // Solo guardar datos de display (NO sensibles) en localStorage
    const displayData = {
      id: user.id,
      nombre: user.nombre,
      email: user.email,
      nickname: user.nickname,
      num_doc: user.num_doc,
      rol: user.rol,
    };
    localStorage.setItem('lp_user_display', JSON.stringify(displayData));
    setUserData(displayData);
    setIsAuthenticated(true);
    if (user.primer_login) setShowChangePasswordModal(true);
  };

  const handleLogout = useCallback(async () => {
    // Llamar al backend para invalidar la sesion del servidor
    try { await api.post('/logout'); } catch (e) { /* ok */ }
    localStorage.removeItem('lp_user_display');
    // Limpiar legacy storage
    localStorage.removeItem('lp_user');
    localStorage.removeItem('userData');
    localStorage.removeItem('isAuthenticated');
    setUserData(null);
    setIsAuthenticated(false);
  }, []);

  const handlePasswordChanged = () => {
    setUserData(prev => {
      const updated = { ...prev, primer_login: false };
      localStorage.setItem('lp_user_display', JSON.stringify(updated));
      return updated;
    });
    setShowChangePasswordModal(false);
  };

  if (!sessionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-night">
        <div className="relative">
          <div className="w-10 h-10 rounded-full border-2 border-brand-indigo/20 border-t-brand-indigo animate-spin" />
          <div className="absolute inset-0 rounded-full bg-brand-indigo/15 blur-xl" />
        </div>
      </div>
    );
  }

  return (
    <Router>
      {showChangePasswordModal && <ChangePasswordModal userId={userData?.id} onPasswordChanged={handlePasswordChanged} />}
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/" replace />} />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/" replace />} />

        <Route path="/*" element={
          isAuthenticated
            ? <LegalPlusSystem user={userData} onLogout={handleLogout} />
            : <Navigate to="/login" replace />
        } />
      </Routes>
    </Router>
  );
};

export default App;
